import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { USERS } from '../store/actionTypes';
import { User, UserInput } from './models/authentication';

const userQuery = `{
  id
  email
  roleId
  preferences {
    id
    defaultDeliveryContact
    subscription {
      subscribedOn
      startsOn
      endsOn
      unsubscribedOn
      status
    }
    paymentMethod
  }
  whitelistId
  phone
  referralCode
  profile {
    firstname
    lastname
    userId
    middlename
    gender
    addresses {
      digital
      streetNumber
      unitNumber
      streetName
      township
      stateOrProvince
      postalCode
      country
      note
    }
    uid
    ids {
      name
      value
    }
    photo
    birthDate
  }
  meta {
    active
    created
    updated
  }
}`;

export const users = createApi({
  reducerPath: USERS,
  baseQuery,
  tagTypes: ['Users'],
  endpoints: builder => ({
    getUser: builder.query<User, { userId: string }>({
      query: variables => ({
        document: gql`
          query User($userId: ObjectID!) {
            user(id: $userId) ${userQuery}
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.user,
    }),
    updateUser: builder.mutation<
      User,
      {
        userId: string;
        userInput: UserInput;
      }
    >({
      query: variables => ({
        document: gql`
          mutation UpdateUser($userId: ObjectID!, $userInput: UserInput!) {
            updateUser(userId: $userId, userInput: $userInput) ${userQuery}
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.updateUser,
    }),
    relatedUsers: builder.query<User[], { userId: string }>({
      query: variables => ({
        document: gql`
          query RelatedUsers($userId: ObjectID!) {
            relatedUsers(id: $userId) ${userQuery}
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.relatedUsers,
    }),
    getMyProfile: builder.query<User, void>({
      query: variables => ({
        document: gql`
          query MyUser {
            myUser {
              id
              email
              roleId
              profile {
                lastname
                firstname
                ids {
                  name
                  value
                }
              }
              preferences {
                notifications {
                  medium
                  status
                }
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.myUser,
    }),
  }),
});

export const {
  useUpdateUserMutation,
  useGetUserQuery,
  useLazyRelatedUsersQuery,
  useRelatedUsersQuery,
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
} = users;
